import { useEffect, useState } from "react";
import {
  getPublicReviews,
  type PublicReviewsSummary,
} from "@/lib/reviews";

/**
 * Resumo público das avaliações para a landing.
 *
 * Estado inicial `null` em vez de um resumo vazio: "ainda não sei" e "não tem
 * nenhuma avaliação" precisam ser distinguíveis, senão o selo do Hero pisca um
 * "0 avaliações" antes de o documento chegar.
 *
 * Erro é silencioso de propósito. Se o Firestore não responder, a landing
 * simplesmente não mostra o selo — prova social é enfeite, não pode derrubar a
 * home nem exibir mensagem de erro para um visitante.
 *
 * O request em si é compartilhado por `getPublicReviews()` (cache de promise no
 * módulo), então o selo do Hero e o bloco de Depoimentos custam uma leitura só.
 */
export function useLandingReviews() {
  const [data, setData] = useState<PublicReviewsSummary | null>(null);

  useEffect(() => {
    let ativo = true;

    getPublicReviews()
      .then((resumo) => {
        if (ativo) setData(resumo);
      })
      .catch(() => {
        /* selo simplesmente não aparece */
      });

    return () => {
      ativo = false;
    };
  }, []);

  const suficiente = !!data && data.total >= MINIMO_PARA_EXIBIR;

  return { data, suficiente };
}

/**
 * Quantas avaliações aprovadas são necessárias para o selo aparecer.
 *
 * Existe porque "★ 5,0 · 1 avaliação" pesa contra: chama atenção para o volume
 * em vez da nota. Suba para 3 ou 5 quando houver massa — é uma linha aqui.
 *
 * ATENÇÃO: `MINIMO_AVALIACOES` em `scripts/seo-static.mjs` precisa ter o MESMO
 * valor. Não é só simetria estética: o `aggregateRating` do JSON-LD é assado no
 * HTML no build, e a política de dado estruturado do Google exige que o que
 * está na marcação esteja visível na página. Se o schema declarar a nota
 * enquanto o selo está escondido por esta trava, a marcação passa a afirmar
 * algo que o visitante não vê.
 */
export const MINIMO_PARA_EXIBIR = 1;
